/**
 * Query helpers for ARI calendar data.
 *
 * Availability and restrictions are both wired. Rate plan IDs for restrictions
 * are resolved via GET /api/v1/rate-plans (see lib/rate_plans.ts).
 */
import { queryOptions } from '@tanstack/react-query';
import { listAvailability, listRestrictions, triggerFullSync, getAriCalendar } from '@/api/generated/sdk.gen';
import type {
  AvailabilityCell,
  RestrictionCell,
  TriggerFullSyncResponse,
  AriCalendarResponse,
  CalendarBooking,
  CalendarHold,
  CalendarRestriction,
} from '@/api/generated/types.gen';

export type {
  AvailabilityCell,
  RestrictionCell,
  TriggerFullSyncResponse,
  AriCalendarResponse,
  CalendarBooking,
  CalendarHold,
  CalendarRestriction,
};

/**
 * Fetch the combined calendar window in ONE request (ADR-033/calendar D1):
 * availability + restrictions + overlapping bookings + holds. `toExclusive` is
 * the exclusive upper bound the endpoint expects (overlap predicate
 * checkin < to AND checkout > from), so pass last-visible-date + 1.
 * Window cap is 90 days server-side.
 */
export function calendarQuery(from: string, toExclusive: string, apartmentIds: string[]) {
  return queryOptions({
    queryKey: ['ari', 'calendar', from, toExclusive, apartmentIds] as const,
    queryFn: async ({ signal }) => {
      const res = await getAriCalendar({
        throwOnError: true,
        signal,
        query: { from, to: toExclusive, apartment_ids: apartmentIds },
      });
      return res.data;
    },
    enabled: apartmentIds.length > 0,
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && count < 1;
    },
    staleTime: 30_000,
  });
}

/** "YYYY-MM-DD" → local Date at midnight (TZ-safe; avoids UTC off-by-one). */
export function ymdToLocalDate(ymd: string): Date {
  const parts = ymd.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Date(y, m - 1, d);
}

/** Format a local Date as YYYY-MM-DD (no timezone conversion). */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** local Date → "YYYY-MM-DD". Alias for toLocalDateString (single source of truth). */
export const localDateToYMD = toLocalDateString;

const DAY_MS = 86_400_000;

/** Parse a YYYY-MM-DD into numeric [y, m, d] (avoids noUncheckedIndexedAccess undefineds). */
function ymdNums(ymd: string): [number, number, number] {
  const p = ymd.split('-');
  return [Number(p[0]), Number(p[1]), Number(p[2])];
}

/** Add n days to a YYYY-MM-DD string (UTC arithmetic, DST-safe), returning YYYY-MM-DD. */
export function addDaysYMD(ymd: string, n: number): string {
  const [y, m, d] = ymdNums(ymd);
  return toLocalDateString(new Date(Date.UTC(y, m - 1, d + n)));
}

/** First day of the month containing ymd → YYYY-MM-01. */
export function firstOfMonthYMD(ymd: string): string {
  const [y, m] = ymdNums(ymd);
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

/** First day of the month n months from ymd's month (n may be negative). */
export function addMonthsYMD(ymd: string, n: number): string {
  const [y, m] = ymdNums(ymd);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = total % 12;
  return `${ny}-${String(nm + 1).padStart(2, '0')}-01`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Human month label for a YYYY-MM-DD, e.g. "August 2026". */
export function monthLabelOf(ymd: string): string {
  const [y, m] = ymdNums(ymd);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** Today as a local YYYY-MM-DD. */
export function todayYMD(): string {
  const now = new Date();
  return toLocalDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
}

/** Whole-day count between two YYYY-MM-DD (b - a). */
export function daysBetweenYMD(a: string, b: string): number {
  const [ay, am, ad] = ymdNums(a);
  const [by, bm, bd] = ymdNums(b);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / DAY_MS);
}

/**
 * Split [fromYMD, toExclYMD) into one chunk per calendar month, each
 * {from: month-start, toExcl: next-month-start}. Each chunk is ≤31 days, safely
 * under the /ari/calendar 90-day server cap, and aligns the fetch cache to months
 * so neighbouring scroll windows reuse the same chunk keys.
 */
export function monthChunks(fromYMD: string, toExclYMD: string): { from: string; toExcl: string }[] {
  const out: { from: string; toExcl: string }[] = [];
  let cur = firstOfMonthYMD(fromYMD);
  // guard against pathological ranges
  for (let i = 0; i < 240 && cur < toExclYMD; i++) {
    const next = addMonthsYMD(cur, 1);
    out.push({ from: cur, toExcl: next });
    cur = next;
  }
  return out;
}

/** Enumerate inclusive calendar dates [from, to] as YYYY-MM-DD strings (UTC arithmetic, DST-safe). */
export function enumerateDates(from: string, to: string): string[] {
  const parse = (s: string): number => {
    const p = s.split('-');
    return Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  };
  const out: string[] = [];
  let cur = parse(from);
  const end = parse(to);
  while (cur <= end) {
    const d = new Date(cur);
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    out.push(`${d.getUTCFullYear()}-${m}-${day}`);
    cur += 86_400_000;
  }
  return out;
}

/**
 * Fetch availability cells for the given apartments over [from, to] (inclusive).
 * Dates must be YYYY-MM-DD UTC strings.
 */
export function availabilityQuery(from: string, to: string, apartmentIds: string[]) {
  return queryOptions({
    queryKey: ['ari', 'availability', from, to, apartmentIds] as const,
    queryFn: async ({ signal }) => {
      const res = await listAvailability({
        throwOnError: true,
        signal,
        query: { from, to, apartment_ids: apartmentIds },
      });
      return res.data.items;
    },
    enabled: apartmentIds.length > 0,
    retry: (count, err) => {
      // Do not retry on 401 — the session is gone.
      const status = (err as { status?: number })?.status;
      return status !== 401 && count < 1;
    },
    staleTime: 30_000,
  });
}

/**
 * Fetch restriction cells for the given rate plans over [from, to] (inclusive).
 * Dates must be YYYY-MM-DD UTC strings.
 * ratePlanIds are resolved via ratePlansQuery in the calendar route.
 */
export function restrictionsQuery(from: string, to: string, ratePlanIds: string[]) {
  return queryOptions({
    queryKey: ['ari', 'restrictions', from, to, ratePlanIds] as const,
    queryFn: async ({ signal }) => {
      const res = await listRestrictions({
        throwOnError: true,
        signal,
        query: { from, to, rate_plan_ids: ratePlanIds },
      });
      return res.data.items;
    },
    enabled: ratePlanIds.length > 0,
    retry: (count, err) => {
      // Mirror availabilityQuery — no retry on 401, otherwise one retry.
      const status = (err as { status?: number })?.status;
      return status !== 401 && count < 1;
    },
    staleTime: 30_000,
  });
}

/**
 * Trigger a full ARI sync (today → +24 months).
 * Body is omitted — the backend defaults to today → +24 months.
 *
 * With throwOnError: true the SDK throws the raw RFC 7807 Problem JSON on
 * non-2xx responses (status, title, detail, code fields).
 */
export async function runFullSync(): Promise<TriggerFullSyncResponse> {
  const res = await triggerFullSync({ throwOnError: true });
  return res.data;
}
